import { useFormik, Form, FormikProvider } from 'formik';
import { Button } from 'styled';
import InputField from 'components/formik/InputField';
import Model from 'components/Modal';
import { getAllData, updateDocumentByID } from 'apis/customApiHandler';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useCallback, useState,useEffect } from 'react';
import ReactSelect from 'react-select';
import { FaTrash } from 'react-icons/fa';
import { IoMdAddCircle } from "react-icons/io";
import { TD } from 'styled/table';

const EditList = ({ handleClick, vendor, desable, showEditModal }) => {

  const { data: tyreSizes, isLoading: isLoadingSizes } = useQuery(
    ['size-master'],
    () => getAllData('/size-master', { searchQuery: '', limit: 250, page: 1 }),
    {
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.error("Error fetching size-master data", err);
        toast.error("Failed to fetch tyre sizes.");
      },
    }
  );

  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData('/customer-fleet', {
      fromUsers: 1,
      limit: 3000,
    })
  );
  const { data: allHubs } = useQuery([`/customer-hub`], () =>
    getAllData('/customer-hub', {
      fromUsers: 1,
      limit: 3000,
    })
  );

  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      vendorCode: vendor?.vendorCode ?? 10,
      vendorName: vendor?.vendorName,
      vendorAddress:vendor?.address,
      vendorCity:vendor?.city,
      vendorRegion:vendor?.regionName,
      effectiveDate:vendor?.effectiveDate?.split('T')[0],
      expiryDate:vendor?.expiryDate?.split('T')[0],
      fleets: vendor?.fleets?.map((fleet) => fleet?._id) ?? [],
      hubs: vendor?.hubs?.map((hub) => hub?._id) ?? [],
      tyreDetails: vendor?.tyreDetails?.map((tyre) => ({
        tyreSize: tyre.tyreSize,
        cost: tyre.cost,
        gstPercentage: tyre.gstPercentage,
        gstAmount: tyre.gstAmount,
        total: tyre.total,
      })) ?? [],
      patchDetails: vendor?.patchDetails?.length ? vendor?.patchDetails?.map((tyre) => ({
        patchSize: tyre.patchSize,
        cost: tyre.cost,
        gstPercentage: tyre.gstPercentage,
        gstAmount: tyre.gstAmount,
        total: tyre.total,
      })) : [],
    },
    onSubmit: async (values) => {
      const payload = {
        effectiveDate: values?.effectiveDate, 
        expiryDate: values?.expiryDate,    
        vendor: values.vendorName,
        hubs: values.hubs,
        fleets: values.fleets,
        tyreDetails: values.tyreDetails,
        patchDetails: values.patchDetails,
        createdBy: vendor?.createdBy,
        renewalReason: vendor?.renewalReason,
        repairCost: vendor?.repairCost
      };

      try {
        const result = await updateDocumentByID('/vendor-contract/update-vendor-master', vendor._id, payload);
        if (result?.success === true) {
          toast.success('Successfully updated');
          queryClient.invalidateQueries('/vendor-contract/update-vendor-master');
          handleClick();
        }
      } catch (error) {
        toast.error('Failed to update. Please try again.');
      }
    },
    validate: (values) => {
      let errors = {};
      if (!values.vendorCode) errors.vendorCode = 'Vendor Code is required';
      // if (!values.vendorName) errors.vendorName = 'Vendor Name is required';
      if (!values.fleets || values.fleets.length === 0) errors.fleets = 'At least one fleet must be selected';
      if (!values.hubs || values.hubs.length === 0) errors.hubs = 'At least one hub must be selected';
      values.tyreDetails.forEach((tyre, index) => {
        if (!tyre.tyreSize) {
          if (!errors.tyreDetails) errors.tyreDetails = [];
          errors.tyreDetails[index] = { tyreSize: 'Tyre size is required' };
        }
        if (!tyre.cost || tyre.cost == 0 || tyre.cost < 0) {
          if (!errors.tyreDetails) errors.tyreDetails = [];
          errors.tyreDetails[index] = { ...errors.tyreDetails[index], cost: 'Please Enter a valid cost' };
        }
      });

      values.patchDetails.forEach((patch, index) => {
        if (!patch.cost || patch.cost <= 0) {
          if (!errors.patchDetails) errors.patchDetails = [];
          errors.patchDetails[index] = { cost: 'Please Enter a valid cost' };
        }
      });
    
      return errors;
    },
  });

  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getHubOptions = useCallback(() => {
    if (allHubs && allHubs.data && allHubs.data.length) {
      return [
        ...(allHubs?.data?.length &&
          allHubs?.data
            ?.filter((hub) => {
              const selectedFleetsVal = allFleets?.data?.filter((fleet) =>
                formik.values.fleets?.includes(fleet._id)
              );

              return selectedFleetsVal?.some(
                (fleet) => fleet.fleetCode === hub.fleetCode
              );
            })
            ?.map((hub) => {
              return {
                value: hub._id,
                label: hub.hubName,
              };
            })),
      ];
    }
  }, [allFleets, allHubs, formik]);

  const handleChange = (e, index, name) => {
    const { value } = e.target;
    const updatedTyres = [...formik.values.tyreDetails];
    updatedTyres[index] = {
      ...updatedTyres[index],
      [name]: value,
    };

    const cost = Number(updatedTyres[index].cost) || 0;
    const gstPercentage = Number(updatedTyres[index].gstPercentage) || 0;
    const gstAmount = Number((cost * gstPercentage) / 100);

    const total = (cost+ gstAmount).toFixed(2);

    updatedTyres[index] = {
      ...updatedTyres[index],
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      total: total,
    };
    formik.setFieldValue('tyreDetails', updatedTyres);
  };

  const handleChangePatch = (e, index, name) => {
    const { value } = e.target;
    const updatedPatches = [...formik.values.patchDetails];
    updatedPatches[index] = {
      ...updatedPatches[index],
      [name]: value,
    };

    const cost = parseFloat(updatedPatches[index].cost) || 0;
    const gstPercentage = parseFloat(updatedPatches[index].gstPercentage) || 0;
    const gstAmount = ((cost * gstPercentage) / 100).toFixed(2);
    const total = (parseFloat(cost) + parseFloat(gstAmount)).toFixed(2);

    updatedPatches[index] = {
      ...updatedPatches[index],
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      total: total,
    };
    formik.setFieldValue('patchDetails', updatedPatches);
  };

  const handleDelete = (index) => {
    const updatedTyres = formik.values.tyreDetails.filter((_, i) => i !== index);
    formik.setFieldValue('tyreDetails', updatedTyres);
  };
  const handleDeletePatch = (index) => {
    const updatedPatch = formik.values.patchDetails.filter((_, i) => i !== index);
    formik.setFieldValue('patchDetails', updatedPatch);
  };

  
  const getAvailableTyreSizes = (currentSize) => {
    const selectedTyreSizes = formik.values.tyreDetails?.map((tyre) => tyre.tyreSize).filter(Boolean);
    return tyreSizes?.data?.filter((size) => 
      size.name === currentSize || !selectedTyreSizes.includes(size.name)
  );
};

const getAvailablePatchSize = (currentPatch) => {
  const selectedPatchSizes = formik.values.patchDetails?.map((patch) => patch.patchSize).filter(Boolean);
  return repariPatchList?.filter((size) => 
      size === currentPatch || !selectedPatchSizes.includes(size)
    );
  };

  const handleAddNew = () => {
    const newTyre = {
      tyreSize: '',
      cost: '',           
      gstPercentage: 18,   
      gstAmount: '',
      total: '', 
    };
    formik.setFieldValue('tyreDetails', [...formik.values.tyreDetails, newTyre]);
  };

  const handleAddNewPatch = () => {
    const newTyre = {
      patchSize: '',
      cost: '',           
      gstPercentage: 18,   
      gstAmount: '',
      total: '', 
    };
    formik.setFieldValue('patchDetails', [...formik.values.patchDetails, newTyre]);
  };

  const handleAddDuplicate = (index) => {
    const tyreToDuplicate = formik.values.tyreDetails[index];
    const duplicatedTyre = { ...tyreToDuplicate };
    formik.setFieldValue('tyreDetails', [...formik.values.tyreDetails, duplicatedTyre]);
  };

  const repariPatchList = [
    'CT-14 Radial',
    'CT-16 Radial',
    'CT-18 Radial',
    'CT-20 Radial',
    'CT-22 Radial',
    'CT-24 Radial',
    'CT-28 Radial',
    'CT-30 Radial',
    'CT-32 Radial',
    'CT-36 Radial',
    'CT-40 Radial',
    'CT-42 Radial',
    'CT-44 Radial',
    'CT-46 Radial',
    'C-1 Bias',
    'C-2 Bias',
    'C-3 Bias',
    'C-4 Bias',
    'C-5 Bias',
    'C-6 Bias',
    'C-7 Bias',
    'C-8 Bias',
    'C-9 Bias',
    'C-10 Bias',
  ];
  

  const isFormValid = Object.keys(formik.errors).length === 0 && formik.values.tyreDetails.every(tyre => tyre.tyreSize && tyre.cost) && formik.values.patchDetails.every(tyre => tyre.patchSize && tyre.cost);

  // useEffect(() => {
  //   if (approvedUpdateVendor && isFormValid) {
  //     formik.handleSubmit();
  //   }
  // }, [approvedUpdateVendor, isFormValid]);
  

  if (isLoadingSizes) {
    return <p>Loading tyre sizes...</p>; 
  }

  return (
    <Model handleClick={handleClick} title={`${desable ? 'View' : 'Edit'} Vendor`} className="max-w-2xl">
      
        <FormikProvider value={formik}>

          <Form className="px-5 text-gray-700 font-medium text-base w-full">
            
          <div className="flex gap-4 flex-wrap">

           <div className="w-full grid grid-cols-3 gap-4">
            <InputField type="text" label="Vendor Name" name="vendorName" desable={true} value={formik.values.vendorName} />
            <InputField type="text" label="Vendor Code" name="vendorCode" desable={true} value={formik.values.vendorCode} />
            <InputField type="text" label="Vendor City" name="vendorCity" desable={true} value={formik.values.vendorCity} />
            </div>

            <div className='w-full grid grid-cols-3 gap-4'>

            <InputField type="text" label="Vendor Address" name="vendorAddress" desable={true} value={formik.values.vendorAddress} />
            <InputField type="text" label="Vendor Region" name="vendorRegion" desable={true} value={formik.values.vendorRegion} />
            
            <InputField type="date" label="Effective Date" name="effectiveDate" 
            max={formik.values.expiryDate}
            desable={desable} value={formik.values.effectiveDate} />

            
            <InputField type="date" 
            min={formik.values.effectiveDate}
            label="Expiry Date" name="expiryDate" desable={desable} value={formik.values.expiryDate} />
            </div>

            <div className="flex flex-col gap-0 text-gray-600 font-normal w-[48%]">
              <label className="py-3">
               Select Fleet <span className="text-red-400">*</span>
                <ReactSelect
                 isDisabled={desable}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: 'transparent',
                      minWidth: '100%',
                      '&:hover': {
                        borderColor: 'rgb(34 197 94)',
                      },
                    }),
                  }}
                  className="shadow-md rounded-md py-1 mb-2"
                  options={getFleetOptions()}
                  value={formik.values.fleets?.map((fleet) => ({
                    value: fleet,
                    label: allFleets?.data?.find((item) => item._id === fleet)?.fleetName,
                  }))}
                  onChange={(value) => {
                    const allValues = value?.map((item) => item.value);
                    if (allValues.includes('all')) {
                      formik.setFieldValue(
                        'fleets',
                        allFleets?.data?.map((item) => item._id)
                      );
                    } else {
                      formik.setFieldValue('fleets', value.map((fleet) => fleet.value));
                    }
                  }}
                  name="fleets"
                  isMulti={true}
                />
              </label>
            </div>

            <div className="flex flex-col gap-0 text-gray-600 font-normal w-[48%]">
              <label className="py-3">
                Select Hubs <span className="text-red-400">*</span>
                <ReactSelect
                isDisabled={desable}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: 'transparent',
                      minWidth: '100%',
                      '&:hover': {
                        borderColor: 'rgb(34 197 94)',
                      },
                    }),
                  }}
                  className="shadow-md rounded-md py-1 mb-2"
                  options={getHubOptions()}
                  value={formik.values.hubs?.map((hub) => ({
                    value: hub,
                    label: allHubs?.data?.find((item) => item._id === hub)?.hubName,
                  }))}
                  onChange={(value) => {
                    formik.setFieldValue('hubs', value.map((hub) => hub.value));
                  }}
                  name="hubs"
                  isMulti={true}
                />
              </label>
            </div>

            </div>

            <div className="col-span-2 overflow-x-auto mt-6 mb-4">

              <h1 className= "mb-2"> Tyre size cost master </h1>

              <table className="min-w-full divide-y divide-gray-200">

                <thead className="text-gray-600 bg-gray-100 rounded-lg">
                  <tr className='rounded-md'>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      Tyre size <span className="text-red-400">*</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      Cost <span className="text-red-400">*</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      GST % <span className="text-red-400">*</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      GST Amount <span className="text-red-400">*</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      Total <span className="text-red-400">*</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="text-gray-600">

                  {formik.values.tyreDetails?.map((tyre, index) => {

                   return <tr key={index} className = "w-full gap-x-2">

                       <TD className="whitespace-nowrap rounded-sm">
                      <select
                        name={`tyreDetails[${index}].tyreSize`}
                        value={tyre.tyreSize}
                        // value={formik.values.tyreDetails[index]?.tyreSize}
                        onChange={(e) => handleChange(e, index, 'tyreSize')}
                        disabled={desable}
                        className="text-gray-600 w-full shadow-md rounded p-2"
                      >
                        <option value="">Select Tyre Size</option>
                        {getAvailableTyreSizes(tyre.tyreSize)?.map((size) => {
                         return <option key={size._id} value={size.name}>
                            {size.name}
                          </option>
                  })}
                      </select>
                        </TD>

                      <TD className="whitespace-nowrap rounded-sm">
                        <InputField
                          type="number"
                          name={`tyreDetails[${index}].cost`}
                          // name= 'cost'
                          desable={desable}
                          value={formik.values.tyreDetails[index]?.cost}
                          className="text-gray-600 w-full shadow-md rounded p-2"
                          onChange={(e) => handleChange(e, index, 'cost')}
                        />
                        </TD>

                      <TD className="whitespace-nowrap rounded-sm">
                        <InputField
                          type="number"
                          name="gstPercentage"
                          desable={true}
                          value={tyre.gstPercentage}
                          className="text-gray-600 w-full shadow-md rounded p-2"
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue >= 0 || newValue === "") {
                              handleChange(e, index, 'gstPercentage');
                            }
                          }}
                        />
                       </TD>

                      <TD className="whitespace-nowrap rounded-sm">
                        <InputField
                          type="number"
                          name={`tyreDetails[${index}].gstAmount`}
                          // name='gstAmount'
                          desable={true}
                          value={formik.values.tyreDetails[index]?.gstAmount}
                          disabled
                          className="text-gray-600 w-full shadow-md rounded p-2"
                        />
                      </TD>

                      <TD className="whitespace-nowrap rounded-sm">
                        <input
                          type="number"
                          name="total"
                          value={tyre.total}
                          disabled
                          className="text-gray-600 w-full shadow-md rounded p-2"
                        />
                      </TD>
                      
                      <TD className="whitespace-nowrap rounded-sm text-center">
                        {
                          showEditModal?(
                          <button
                          type="button"
                          onClick={() => handleDelete(index)}
                          className="p-2 m-1 rounded-md bg-red-500 hover:bg-red-600 text-white justify-center items-center"
                        >
                          <FaTrash />
                          </button>

                          ): (
                            <div className="flex justify-center items-center"> --- </div>
                          )}

                      </TD>

                    </tr>
})}
                </tbody>

              </table>
              
              {showEditModal && <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={handleAddNew}
                disabled={desable}
                className="p-2 text-lg rounded-md bg-yellow-500 hover:bg-orange-300 text-white flex items-right justify-right float-right"
              >
                <IoMdAddCircle/>
              </button>
            </div>}
            </div>

          <div className="col-span-2 overflow-x-auto mt-6 mb-4">
            <h1 className= "mb-2"> Patch Cost Master </h1>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="text-gray-600 bg-gray-100 rounded-lg">
                <tr className='rounded-md'>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    Patch size <span className="text-red-400">*</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    Cost <span className="text-red-400">*</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    GST % <span className="text-red-400">*</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    GST Amount <span className="text-red-400">*</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    Total <span className="text-red-400">*</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                {formik.values.patchDetails?.map((tyre, index) => {
                  return <tr key={index} className="w-full gap-x-2">
                    <TD className="whitespace-nowrap rounded-sm">
                      <select
                        name={`patchDetails[${index}].patchSize`}
                        value={tyre.patchSize}
                        // value={formik.values.patchDetails[index]?.patchSize}
                        onChange={(e) => handleChangePatch(e, index, 'patchSize')}
                        disabled={desable}
                        className="text-gray-600 w-full shadow-md rounded p-2"
                      >
                        <option value="">Select Patch Size</option>
                        {getAvailablePatchSize(tyre.patchSize)?.map((size) => {
                          return <option key={size} value={size}>
                            {size}
                          </option>
                        })}
                      </select>
                    </TD>
                    <TD className="whitespace-nowrap rounded-sm">
                      <InputField
                        type="number"
                        name={`patchDetails[${index}].cost`}
                        desable={desable}
                        value={formik.values.patchDetails[index]?.cost}
                        className="text-gray-600 w-full shadow-md rounded p-2"
                        onChange={(e) => handleChangePatch(e, index, 'cost')}
                      />
                    </TD>
                    <TD className="whitespace-nowrap rounded-sm">
                      <InputField
                        type="number"
                        name="gstPercentage"
                        desable={true}
                        value={tyre.gstPercentage}
                        className="text-gray-600 w-full shadow-md rounded p-2"
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue >= 0 || newValue === "") {
                            handleChangePatch(e, index, 'gstPercentage');
                          }
                        }}
                      />
                    </TD>
                    <TD className="whitespace-nowrap rounded-sm">
                      <InputField
                        type="number"
                        name={`patchDetails[${index}].gstAmount`}
                        // name='gstAmount'
                        value={formik.values.patchDetails[index]?.gstAmount}
                        desable={true}
                        className="text-gray-600 w-full shadow-md rounded p-2"
                      />
                    </TD>
                    <TD className="whitespace-nowrap rounded-sm">
                      <input
                        type="number"
                        name="total"
                        value={tyre.total}
                        disabled
                        className="text-gray-600 w-full shadow-md rounded p-2"
                      />
                    </TD>

                      <TD className="whitespace-nowrap rounded-sm text-center">
                    {showEditModal ? 
                      <button
                        type="button"
                        onClick={() => handleDeletePatch(index)}
                        className="p-2 m-1 rounded-md bg-red-500 hover:bg-red-600 text-white justify-center items-center"
                      >
                        <FaTrash />
                      </button>
                     : <div className='text-center'> --- </div>}
                     </TD>

                  </tr>
                })}
              </tbody>
            </table>
            
            {showEditModal && <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={handleAddNewPatch}
                disabled={desable}
                className="p-2 text-lg rounded-md bg-yellow-500 hover:bg-orange-300 text-white flex items-right justify-right float-right"
              >
                <IoMdAddCircle/>
              </button>
            </div>}
          </div>

           {showEditModal && <div className="px-5 flex justify-end items-center gap-4 pb-8 flex-wrap sm:w-full">
            <Button bgSecondary onClick={handleClick}>
              Cancel
            </Button>
            <Button type="submit" bgPrimary disabled={!isFormValid || !formik.values.tyreDetails?.length}>
              Save Changes
            </Button>
          </div>}

          </Form>

         
        </FormikProvider>
      
    </Model>
  );
};

export default EditList;